import React from 'react';
import { View, Text } from 'react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import { LucideIcon } from 'lucide-react-native';

interface SectionDividerProps {
  /** Optional label to display */
  label?: string;
  /** Optional icon to show before the label */
  icon?: LucideIcon;
  /** Whether to show a line on the left side of the label */
  showLeftLine?: boolean;
  /** Custom margin top */
  marginTop?: number;
  /** Custom margin bottom */
  marginBottom?: number;
}

/**
 * A subtle section divider to help guide users visually through page sections.
 * Can be used as a simple line or with a label/icon.
 */
export function SectionDivider({
  label,
  icon: Icon,
  showLeftLine = false,
  marginTop = 24,
  marginBottom = 16,
}: SectionDividerProps) {
  const colors = useThemeColors();

  // Simple line divider without label
  if (!label) {
    return (
      <View
        style={{
          height: 1,
          backgroundColor: colors.border.dark,
          marginTop,
          marginBottom,
        }}
      />
    );
  }

  // Divider with label (and optional icon).
  // Typography harmonized with ScoreSectionDivider: text.primary / weight 700 /
  // letterSpacing 1.4 / uppercase. Higher contrast so section boundaries scan
  // clearly even on dense pages.
  return (
    <View
      className="flex-row items-center"
      style={{ marginTop, marginBottom, gap: 12 }}
    >
      {showLeftLine && (
        <View
          className="flex-1"
          style={{
            height: 1,
            backgroundColor: colors.border.dark,
          }}
        />
      )}
      <View className="flex-row items-center gap-2">
        {Icon && <Icon size={14} color={colors.text.primary} />}
        <Text
          style={{
            color: colors.text.primary,
            fontSize: 12,
            fontWeight: '700',
            letterSpacing: 1.4,
            textTransform: 'uppercase',
            flexShrink: 0,
          }}
          numberOfLines={1}
        >
          {label}
        </Text>
      </View>
      <View
        className="flex-1"
        style={{
          height: 1,
          backgroundColor: colors.border.dark,
        }}
      />
    </View>
  );
}
